import React, { useEffect, useState } from 'react';
import {
  playStickyAudio,
  resolveStickyAudioUrl,
  subscribeStickyAudioPlayback
} from '../../utils/stickyAudioPlayback';

type VocabPronounceSize = 'sm' | 'md' | 'lg';

interface VocabPronounceButtonProps {
  url?: string | null;
  ariaLabel: string;
  size?: VocabPronounceSize;
  className?: string;
}

const SIZE: Record<VocabPronounceSize, { button: string; icon: string }> = {
  sm: { button: 'p-0.5', icon: 'h-3 w-3' },
  md: { button: 'p-1', icon: 'h-3.5 w-3.5' },
  lg: { button: 'p-2', icon: 'h-5 w-5' }
};

const VocabSpeakIcon: React.FC<{ className?: string; playing: boolean }> = ({ className, playing }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`vocab-speak-icon ${playing ? 'is-playing' : ''} ${className ?? ''}`}
    aria-hidden
  >
    <path
      className="vocab-speak-body"
      d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z"
    />
    <path className="vocab-speak-wave vocab-speak-wave--1" d="M16 9a5 5 0 0 1 0 6" />
    <path className="vocab-speak-wave vocab-speak-wave--2" d="M19.364 18.364a9 9 0 0 0 0-12.728" />
  </svg>
);

export const VocabPronounceButton: React.FC<VocabPronounceButtonProps> = ({
  url,
  ariaLabel,
  size = 'sm',
  className = ''
}) => {
  const [playing, setPlaying] = useState(false);
  const resolved = url ? resolveStickyAudioUrl(url) : '';
  const dims = SIZE[size];

  useEffect(() => {
    if (!resolved) {
      setPlaying(false);
      return undefined;
    }
    return subscribeStickyAudioPlayback((active) => {
      setPlaying(Boolean(active && active === resolved));
    });
  }, [resolved]);

  return (
    <button
      type="button"
      disabled={!resolved}
      aria-label={ariaLabel}
      aria-pressed={playing}
      className={`vocab-pronounce ${dims.button} shrink-0 rounded-full bg-rose-100 text-rose-700 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-rose-950/60 dark:text-rose-200 ${
        playing ? 'is-playing' : ''
      } ${className}`}
      onClick={(event) => {
        event.stopPropagation();
        if (url) playStickyAudio(url);
      }}
    >
      <VocabSpeakIcon className={dims.icon} playing={playing} />
    </button>
  );
};
